/**
 * Video Edit Draft Service
 *
 * Manages original videos and their edit drafts in Firestore.
 *
 * Structure (context-based):
 *   adminVideos/{videoId}                           # Admin test videos
 *   lessons/{lessonId}/videos/{videoId}             # Lesson videos
 *   .../drafts/{draftId}                            # Video edit drafts
 *
 * Firebase Storage:
 *   adminVideos/{videoId}/(files/drafts)
 *   lessons/{lessonId}/videos/{videoId}/(files/drafts)
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  runTransaction,
} from 'firebase/firestore';
import type { Unsubscribe } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { transcodeClip } from '../utils/videoProcessing';
import type {
  OriginalVideo,
  VideoEditDraft,
  VideoEditDraftSummary,
  StoredNotableSegment,
  NotableSegment,
  StoredVoiceoverNotableSegmentClip,
  VoiceoverNotableSegmentClip,
  VideoContext,
} from '../types/videoEdit';
import type { LessonDrillRef } from '../types/lesson';
import { isEmptyPlan, refuseEmptyOverwrite } from './draftGuard';
import { clipNeedsUpload, draftPatch } from './draftPatch';

// ============================================
// Path Helper Functions
// ============================================

/**
 * Get the Firestore collection path for videos based on context
 * Returns the path to the collection containing video documents
 *
 * Firestore paths must have even segments (collection/doc pairs):
 * - Admin: adminVideos/{videoId}
 * - Conversation: conversations/{conversationId}/originalVideos/{videoId} (existing structure)
 * - Lesson: lessons/{lessonId}/videos/{videoId}
 */
export function getVideosCollectionPath(context: VideoContext): string {
  switch (context.type) {
    case 'admin':
      return 'adminVideos';
    case 'lesson':
      if (!context.id) throw new Error('Lesson context requires an id');
      return `lessons/${context.id}/videos`;
    default:
      throw new Error(`Unknown context type: ${(context as VideoContext).type}`);
  }
}

/**
 * Get the Firebase Storage base path for a video
 */
export function getStorageBasePath(context: VideoContext, videoId: string): string {
  switch (context.type) {
    case 'admin':
      return `adminVideos/${videoId}`;
    case 'lesson':
      if (!context.id) throw new Error('Lesson context requires an id');
      return `lessons/${context.id}/videos/${videoId}`;
    default:
      throw new Error(`Unknown context type: ${(context as VideoContext).type}`);
  }
}

/**
 * Get the Firebase Storage path for draft assets
 */
export function getDraftStoragePath(
  context: VideoContext,
  videoId: string,
  draftId: string
): string {
  return `${getStorageBasePath(context, videoId)}/drafts/${draftId}`;
}


// ============================================
// Original Video Operations
// ============================================

/**
 * Create an original video document
 * @param videoId - Optional custom ID for the video. If not provided, generates one with timestamp.
 */
export async function createOriginalVideo(
  context: VideoContext,
  uploaderId: string,
  uploaderName: string,
  storagePath: string,
  duration: number,
  playerMessage?: string,
  title?: string,
  description?: string,
  videoId?: string,
  downloadUrl?: string
): Promise<OriginalVideo> {
  const finalVideoId = videoId || `video_${Date.now()}`;
  const now = new Date().toISOString();

  // Build the video object, only including optional fields if they have values
  // Firestore doesn't allow undefined values
  const video: OriginalVideo = {
    id: finalVideoId,
    context,
    uploaderId,
    uploaderName,
    storagePath,
    duration,
    status: 'ready',
    createdAt: now,
    ...(title !== undefined && { title }),
    ...(description !== undefined && { description }),
    ...(playerMessage !== undefined && { playerMessage }),
    ...(downloadUrl !== undefined && { downloadUrl }),
  };

  const collectionPath = getVideosCollectionPath(context);
  const videoRef = doc(db, collectionPath, finalVideoId);
  await setDoc(videoRef, video);

  return video;
}

/**
 * Update an original video's metadata
 */
export async function updateOriginalVideo(
  context: VideoContext,
  videoId: string,
  updates: Partial<Pick<OriginalVideo, 'title' | 'description' | 'playerMessage'>>
): Promise<void> {
  // Filter out undefined values - Firestore doesn't allow them
  const filteredUpdates: Record<string, string> = {};
  for (const [key, value] of Object.entries(updates)) {
    if (value !== undefined) {
      filteredUpdates[key] = value;
    }
  }

  if (Object.keys(filteredUpdates).length === 0) {
    return; // Nothing to update
  }

  const collectionPath = getVideosCollectionPath(context);
  const videoRef = doc(db, collectionPath, videoId);
  await updateDoc(videoRef, filteredUpdates);
}

/**
 * Delete an original video and all its drafts
 */
export async function deleteOriginalVideo(
  context: VideoContext,
  videoId: string
): Promise<void> {
  // First, get and delete all drafts
  const drafts = await getDrafts(context, videoId);
  for (const draft of drafts) {
    await deleteDraft(context, videoId, draft.id);
  }

  // Delete the video's storage file
  const video = await getOriginalVideo(context, videoId);
  if (video?.storagePath) {
    try {
      const storageRef = ref(storage, video.storagePath);
      await deleteObject(storageRef);
    } catch (error) {
      console.warn('Failed to delete video storage file:', video.storagePath, error);
    }
  }

  // Delete the video document
  const collectionPath = getVideosCollectionPath(context);
  const videoRef = doc(db, collectionPath, videoId);
  await deleteDoc(videoRef);
}

/**
 * Get an original video by ID
 */
export async function getOriginalVideo(
  context: VideoContext,
  videoId: string
): Promise<OriginalVideo | null> {
  const collectionPath = getVideosCollectionPath(context);
  const videoRef = doc(db, collectionPath, videoId);
  const snapshot = await getDoc(videoRef);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.data() as OriginalVideo;
}

/**
 * Get all original videos for a context
 */
export async function getOriginalVideos(
  context: VideoContext
): Promise<OriginalVideo[]> {
  const collectionPath = getVideosCollectionPath(context);
  const videosRef = collection(db, collectionPath);
  const q = query(videosRef, where('status', '==', 'ready'), orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => doc.data() as OriginalVideo);
}

/**
 * Get download URL for an original video
 */
export async function getOriginalVideoUrl(
  video: OriginalVideo
): Promise<string> {
  if (video.downloadUrl) {
    return video.downloadUrl;
  }

  const storageRef = ref(storage, video.storagePath);
  return getDownloadURL(storageRef);
}

// ============================================
// Draft Operations
// ============================================

/**
 * Upload a blob to Firebase Storage and return the storage path
 */
async function uploadBlob(blob: Blob, storagePath: string): Promise<string> {
  // A valid WebM recording is always several kilobytes minimum. Anything smaller
  // is an empty or corrupt blob — reject early rather than storing garbage.
  if (blob.size < 4096) {
    throw new Error(`uploadBlob: blob is too small (${blob.size} bytes). Likely corrupt or empty`);
  }
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, blob);
  return storagePath;
}

/**
 * Get download URL for a storage path
 */
export async function getStorageUrl(storagePath: string): Promise<string> {
  const storageRef = ref(storage, storagePath);
  return getDownloadURL(storageRef);
}

/**
 * Delete a file from storage
 */
async function deleteStorageFile(storagePath: string): Promise<void> {
  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
  } catch (error) {
    console.warn('Failed to delete storage file:', storagePath, error);
  }
}

/**
 * Get video blob duration.
 *
 * MediaRecorder WebM blobs report duration=Infinity in loadedmetadata.
 * We use the seek-to-MAX_SAFE_INTEGER trick to let the browser scan to the
 * end of the file and report the real currentTime as the duration.
 */
async function getVideoBlobDuration(blob: Blob): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'metadata';

    let settled = false;
    const cleanup = (dur: number) => {
      if (settled) return;
      settled = true;
      // Set src to empty BEFORE revoking — prevents ERR_FILE_NOT_FOUND.
      video.src = '';
      video.load();
      URL.revokeObjectURL(url);
      resolve(dur);
    };

    // 10-second safety net in case neither seeked nor onerror fires
    const timeoutId = setTimeout(() => cleanup(0), 10_000);

    video.onloadedmetadata = () => {
      if (isFinite(video.duration) && video.duration > 0) {
        clearTimeout(timeoutId);
        cleanup(video.duration);
      } else {
        // Infinity duration (MediaRecorder WebM) — seek to end to get real duration
        video.onseeked = () => {
          clearTimeout(timeoutId);
          cleanup(video.currentTime);
        };
        video.currentTime = Number.MAX_SAFE_INTEGER;
      }
    };
    video.onerror = () => { clearTimeout(timeoutId); cleanup(0); };
    video.src = url;
    video.load();
  });
}

/**
 * Normalize a raw Firestore draft document to the current VideoEditDraft shape.
 * Handles backward compatibility for renamed fields.
 */
function normalizeDraft(data: Record<string, unknown>): VideoEditDraft {
  const d = data as Record<string, unknown>;

  // Backward compat: slowMotionSegments → notableSegments
  if (!d['notableSegments'] && d['slowMotionSegments']) {
    d['notableSegments'] = d['slowMotionSegments'];
  }

  // Backward compat: slowMotionVoiceoverClips → voiceoverNotableSegmentClips
  // Old clips were audio-only — convert to new shape without videoStoragePath
  if (!d['voiceoverNotableSegmentClips'] && d['slowMotionVoiceoverClips']) {
    const oldClips = d['slowMotionVoiceoverClips'] as Array<Record<string, unknown>>;
    d['voiceoverNotableSegmentClips'] = oldClips.map(clip => ({
      id: clip['id'],
      insertionPoint: clip['insertionPoint'],
      segmentIds: clip['segmentId'] ? [clip['segmentId']] : [],
      // no videoStoragePath — old clips used audioStoragePath which is incompatible
    }));
  }

  // Ensure arrays are always present
  if (!d['notableSegments']) d['notableSegments'] = [];
  if (!d['voiceoverNotableSegmentClips']) d['voiceoverNotableSegmentClips'] = [];

  // Hard-reset legacy fields — the new editor no longer uses them
  d['recordingSegments'] = [];
  d['annotationSnapshots'] = [];
  d['audioRecordings'] = [];
  d['clipMasks'] = [];

  return d as unknown as VideoEditDraft;
}

/**
 * Upload voiceover clip assets to Storage
 */
export async function uploadEditAssets(
  context: VideoContext,
  originalVideoId: string,
  draftId: string,
  voiceoverNotableSegmentClips: VoiceoverNotableSegmentClip[] = [],
  skipUploaded = false
): Promise<{
  storedVoiceoverClips: StoredVoiceoverNotableSegmentClip[];
}> {
  const basePath = getDraftStoragePath(context, originalVideoId, draftId);

  // Upload voiceover notable segment clips (video blobs)
  const storedVoiceoverClips: StoredVoiceoverNotableSegmentClip[] = [];
  for (const clip of voiceoverNotableSegmentClips) {
    /* skipUploaded (auto-save): a take that already has its storage path was
       uploaded by an earlier save. The editor keeps its blob for playback, and
       re-sending it on every save is what made a phone save take 22 to 42s. */
    if (clip.videoBlob && !(skipUploaded && !clipNeedsUpload(clip))) {
      const webmPath = `${basePath}/notable-clips/${clip.id}.webm`;
      // uploadBlob throws if the blob is suspiciously small (corrupt/empty)
      await uploadBlob(clip.videoBlob, webmPath);
      // Use wall-clock duration when positive — it's set in recorder.onstop from Date.now()
      // and is always accurate for a normal recording. Fall back to the blob-scan probe
      // when wall-clock is missing or zero (should not happen in practice).
      const wallClock = clip.videoDuration;
      const duration = (wallClock !== undefined && wallClock > 0)
        ? wallClock
        : await getVideoBlobDuration(clip.videoBlob);
      if (!(duration > 0)) {
        throw new Error(`uploadEditAssets: clip ${clip.id} has no valid duration. The blob may be corrupt`);
      }
      // Cloud Run's composition pipeline downloads and re-transcodes every clip
      // with FFmpeg, so it handles WebM inputs natively. We use the WebM path
      // directly so the submission doesn't block on a Cloud Run transcode call.
      // Transcoding to MP4 still happens in the background so native-app clip
      // previews eventually work; it does NOT gate delivery.
      transcodeClip(webmPath).catch((err) => {
        console.warn('[videoEditDraft] Background transcodeClip failed:', err);
      });
      const storagePath = webmPath;
      storedVoiceoverClips.push({
        id: clip.id,
        insertionPoint: 0,
        segmentIds: clip.segmentIds,
        ...(clip.isCameraSource && { isCameraSource: true }),
        videoDuration: duration,
        videoStoragePath: storagePath,
        ...(clip.thumbnailDataUrl && { thumbnailDataUrl: clip.thumbnailDataUrl }),
        ...(clip.label && { label: clip.label }),
      });
    } else if (clip.videoStoragePath) {
      // Already uploaded — preserve as-is
      storedVoiceoverClips.push({
        id: clip.id,
        insertionPoint: 0,
        segmentIds: clip.segmentIds,
        ...(clip.isCameraSource && { isCameraSource: true }),
        ...(clip.videoDuration !== undefined && { videoDuration: clip.videoDuration }),
        videoStoragePath: clip.videoStoragePath,
        ...(clip.thumbnailDataUrl && { thumbnailDataUrl: clip.thumbnailDataUrl }),
        ...(clip.label && { label: clip.label }),
      });
    }
  }

  return { storedVoiceoverClips };
}

/**
 * The clip fields this client writes. Anything else on a stored clip was
 * written by the server (the processor records `videoMp4StoragePath` once
 * its background transcode lands) and must survive a re-save.
 */
const CLIENT_CLIP_KEYS = new Set<string>([
  'id', 'insertionPoint', 'segmentIds', 'isCameraSource', 'videoDuration',
  'videoStoragePath', 'thumbnailDataUrl', 'label',
]);

/**
 * Carries server-written fields over from the stored draft onto the clips
 * about to be saved, matched by id and storage path. Clip ids are new for
 * every recording, so a matching clip is the same file. Fields this client
 * owns always come from `clips`, so clearing a label still clears it.
 */
export function withServerClipFields(
  clips: StoredVoiceoverNotableSegmentClip[],
  stored: StoredVoiceoverNotableSegmentClip[],
): StoredVoiceoverNotableSegmentClip[] {
  const storedById = new Map(stored.map((c) => [c.id, c]));
  return clips.map((clip) => {
    const previous = storedById.get(clip.id);
    if (!previous || previous.videoStoragePath !== clip.videoStoragePath) return clip;
    const serverFields = Object.fromEntries(
      Object.entries(previous).filter(([key]) => !CLIENT_CLIP_KEYS.has(key)),
    );
    return { ...serverFields, ...clip };
  });
}

export interface SaveVideoEditDraftOptions {
  userId: string;
  userName: string;
  context: VideoContext;
  originalVideoId: string;
  audioMuted: boolean;
  /** Player-facing editor notes. */
  notes: string;
  /** Omit to create a new draft under the id 'current'. */
  existingDraftId?: string;
  notableSegments?: NotableSegment[];
  voiceoverNotableSegmentClips?: VoiceoverNotableSegmentClip[];
  /** Private coach-only note. Never written to the lesson document. */
  coachNotes?: string;
  attachedDrills?: LessonDrillRef[];
  videoRotation?: number;
  /**
   * Set only by an editor session that loaded a non-empty plan: an empty plan
   * written now is the coach's own deletion. See draftGuard.ts.
   */
  allowEmptyOverwrite?: boolean;
  /**
   * The editor's auto-save (draftPatch.ts): takes already uploaded are not
   * sent again, and a save whose takes are unchanged writes only the fields
   * that changed, without reading first. Submission leaves this off.
   */
  autosave?: boolean;
}

/**
 * The draft as this client last read or wrote it, by document path. The
 * auto-save patches against it; anything it cannot patch takes the full path.
 */
const lastKnownDrafts = new Map<string, VideoEditDraft>();

/** Thrown instead of writing an empty plan over a stored plan that has content. */
export class EmptyDraftOverwriteError extends Error {
  constructor() {
    super('Refused to save an empty lesson plan over a saved one. Reopen the lesson to load your plan.');
    this.name = 'EmptyDraftOverwriteError';
  }
}

/**
 * Save or update a video edit draft.
 *
 * The full path writes with setDoc and no merge, so this object is the WHOLE
 * document: anything not listed here is removed from the draft. An auto-save
 * whose takes are unchanged patches the changed fields instead (`autosave`).
 */
export async function saveVideoEditDraft(
  options: SaveVideoEditDraftOptions
): Promise<VideoEditDraft> {
  const {
    userId,
    userName,
    context,
    originalVideoId,
    audioMuted,
    notes,
    existingDraftId,
    notableSegments = [],
    voiceoverNotableSegmentClips = [],
    coachNotes,
    attachedDrills,
    videoRotation,
    allowEmptyOverwrite = false,
    autosave = false,
  } = options;

  const draftId = existingDraftId || 'current';
  const now = new Date().toISOString();
  const draftRef = doc(db, getVideosCollectionPath(context), originalVideoId, 'drafts', draftId);

  /* Auto-save, takes unchanged: write the changed fields only. An empty plan
     always takes the full path, which reads and applies the guard. */
  const known = autosave ? lastKnownDrafts.get(draftRef.path) : undefined;
  if (known) {
    const incoming = { notableSegments, voiceoverNotableSegmentClips, notes, audioMuted, coachNotes, attachedDrills, videoRotation };
    const patch = isEmptyPlan(incoming) ? null : draftPatch(known, incoming);
    if (patch) {
      const fields = { ...patch, authorId: userId, authorName: userName, updatedAt: now, lastEditedAt: now };
      try {
        await updateDoc(draftRef, fields);
        const next = { ...known, ...fields } as VideoEditDraft;
        lastKnownDrafts.set(draftRef.path, next);
        return next;
      } catch (err) {
        // Gone, or refused: the full path below reads and rewrites it.
        console.warn('[saveVideoEditDraft] patch failed, writing the whole draft', err);
        lastKnownDrafts.delete(draftRef.path);
      }
    }
  }

  /* Always read what is stored under this id, whether or not the caller
     knew a draft existed. A host that opened the editor before its draft
     arrived passes no existingDraftId; skipping the read then skipped the
     clip merge below and the guard, and the setDoc replaced the whole plan. */
  let createdAt = now;
  const existingDraft: VideoEditDraft | null = await readDraftNow(context, originalVideoId, draftId);
  if (existingDraft) {
    createdAt = existingDraft.createdAt;
  }
  const incomingPlan = { notableSegments, voiceoverNotableSegmentClips, notes, coachNotes, attachedDrills };
  if (refuseEmptyOverwrite(incomingPlan, existingDraft, allowEmptyOverwrite)) {
    console.warn('[saveVideoEditDraft] refused an empty plan over a non-empty draft', { context, originalVideoId, draftId });
    throw new EmptyDraftOverwriteError();
  }

  // Upload new clip blobs; preserve already-uploaded clips
  const { storedVoiceoverClips: newVoiceoverClips } = await uploadEditAssets(
    context,
    originalVideoId,
    draftId,
    voiceoverNotableSegmentClips,
    autosave
  );

  // Merge: keep existing uploaded clips not replaced by a new blob
  let finalVoiceoverClips: StoredVoiceoverNotableSegmentClip[] = newVoiceoverClips;
  if (existingDraft) {
    const newIds = new Set(newVoiceoverClips.map((c) => c.id));
    const incomingIds = new Set(voiceoverNotableSegmentClips.map((c) => c.id));
    // Clips the editor explicitly removed (not in incomingIds) are intentionally dropped.
    // Clips the editor knows about but that uploadEditAssets silently dropped (in
    // incomingIds but not in newIds) are pulled from the existing draft so they are
    // not lost: this happens when a clip has neither a videoBlob nor a videoStoragePath
    // in the incoming state, e.g. due to a partial state load.
    const existingToKeep = existingDraft.voiceoverNotableSegmentClips.filter(
      (clip) => !newIds.has(clip.id) && incomingIds.has(clip.id)
    );
    // Warn if incoming clips are being dropped entirely (not rescued from existing draft).
    // This indicates the editor's state had clips that uploadEditAssets could not handle.
    const droppedIds = voiceoverNotableSegmentClips
      .filter((c) => !newIds.has(c.id) && !existingDraft.voiceoverNotableSegmentClips.some((e) => e.id === c.id))
      .map((c) => c.id);
    if (droppedIds.length > 0) {
      console.warn('[saveVideoEditDraft] incoming clips with no blob and no storagePath — dropped:', droppedIds);
    }
    finalVoiceoverClips = withServerClipFields(
      [...newVoiceoverClips, ...existingToKeep],
      existingDraft.voiceoverNotableSegmentClips,
    );
  }

  // Notable segments stored as-is
  const storedNotableSegments: StoredNotableSegment[] = notableSegments.map((seg) => ({
    id: seg.id,
    label: seg.label,
    startTime: seg.startTime,
    endTime: seg.endTime,
    ...(seg.note !== undefined && { note: seg.note }),
  }));

  const draft: VideoEditDraft = {
    id: draftId,
    originalVideoId,
    context,
    authorId: userId,
    authorName: userName,
    // Legacy fields hard-reset to empty
    recordingSegments: [],
    annotationSnapshots: [],
    audioRecordings: [],
    clipMasks: [],
    notableSegments: storedNotableSegments,
    voiceoverNotableSegmentClips: finalVoiceoverClips,
    audioMuted,
    notes,
    status: 'draft',
    createdAt,
    updatedAt: now,
    lastEditedAt: now,
    // Conditionally spread: Firestore rejects undefined values.
    ...(coachNotes !== undefined && { coachNotes }),
    ...(attachedDrills !== undefined && { attachedDrills }),
    ...(videoRotation !== undefined && { videoRotation }),
  };

  await setDoc(draftRef, draft);
  lastKnownDrafts.set(draftRef.path, draft);

  return draft;
}

/**
 * Get a specific draft
 */
export async function getDraft(
  context: VideoContext,
  originalVideoId: string,
  draftId: string
): Promise<VideoEditDraft | null> {
  const collectionPath = getVideosCollectionPath(context);
  const draftRef = doc(
    db,
    collectionPath,
    originalVideoId,
    'drafts',
    draftId
  );
  const snapshot = await getDoc(draftRef);

  if (!snapshot.exists()) {
    lastKnownDrafts.delete(draftRef.path);
    return null;
  }

  const draft = normalizeDraft(snapshot.data() as Record<string, unknown>);
  lastKnownDrafts.set(draftRef.path, draft);
  return draft;
}

/**
 * The draft as stored now, read in one request.
 *
 * getDoc reads through the listen stream, and on a phone (iOS Safari) that
 * read was measured at 4 to 30 seconds while writes on the write stream came
 * back in about 50ms. It held every full save and kept the editor on
 * Loading for up to 29s on open. A transaction's read is a single request
 * to the server, so it is used wherever the editor waits on the draft. It
 * only reads. If it fails (offline), getDoc is the fallback.
 */
export async function readDraftNow(
  context: VideoContext,
  originalVideoId: string,
  draftId: string
): Promise<VideoEditDraft | null> {
  const draftRef = doc(db, getVideosCollectionPath(context), originalVideoId, 'drafts', draftId);
  let data: Record<string, unknown> | null;
  try {
    data = await runTransaction(db, async (tx) => {
      const snap = await tx.get(draftRef);
      return snap.exists() ? (snap.data() as Record<string, unknown>) : null;
    });
  } catch (err) {
    console.warn('[readDraftNow] transaction read failed, reading through getDoc', err);
    return getDraft(context, originalVideoId, draftId);
  }
  if (!data) {
    lastKnownDrafts.delete(draftRef.path);
    return null;
  }
  const draft = normalizeDraft(data);
  lastKnownDrafts.set(draftRef.path, draft);
  return draft;
}

/**
 * Get the latest draft for an original video
 */
export async function getLatestDraft(
  context: VideoContext,
  originalVideoId: string,
  authorId?: string
): Promise<VideoEditDraft | null> {
  const collectionPath = getVideosCollectionPath(context);
  const draftsRef = collection(
    db,
    collectionPath,
    originalVideoId,
    'drafts'
  );

  let q = query(draftsRef, orderBy('updatedAt', 'desc'), limit(1));

  if (authorId) {
    q = query(
      draftsRef,
      where('authorId', '==', authorId),
      orderBy('updatedAt', 'desc'),
      limit(1)
    );
  }

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  return normalizeDraft(snapshot.docs[0].data() as Record<string, unknown>);
}

/**
 * Get all drafts for an original video
 */
export async function getDrafts(
  context: VideoContext,
  originalVideoId: string
): Promise<VideoEditDraft[]> {
  const collectionPath = getVideosCollectionPath(context);
  const draftsRef = collection(
    db,
    collectionPath,
    originalVideoId,
    'drafts'
  );
  const q = query(draftsRef, orderBy('updatedAt', 'desc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => normalizeDraft(doc.data() as Record<string, unknown>));
}

/**
 * Get draft summaries for an original video (lightweight)
 */
export async function getDraftSummaries(
  context: VideoContext,
  originalVideoId: string
): Promise<VideoEditDraftSummary[]> {
  const drafts = await getDrafts(context, originalVideoId);

  return drafts.map((draft) => ({
    id: draft.id,
    originalVideoId: draft.originalVideoId,
    context: draft.context,
    authorId: draft.authorId,
    authorName: draft.authorName,
    status: draft.status,
    hasRecordings:
      draft.recordingSegments.length > 0 || draft.audioRecordings.length > 0,
    hasAnnotations: draft.annotationSnapshots.length > 0,
    hasClipMasks: draft.clipMasks.length > 0,
    updatedAt: draft.updatedAt,
    publishedAt: draft.publishedAt,
  }));
}

/**
 * Update draft status
 */
export async function updateDraftStatus(
  context: VideoContext,
  originalVideoId: string,
  draftId: string,
  status: VideoEditDraft['status'],
  additionalData?: Partial<VideoEditDraft>
): Promise<void> {
  const collectionPath = getVideosCollectionPath(context);
  const draftRef = doc(
    db,
    collectionPath,
    originalVideoId,
    'drafts',
    draftId
  );
  await updateDoc(draftRef, {
    status,
    updatedAt: new Date().toISOString(),
    ...additionalData,
  });
}

/**
 * Publish a draft for the player to view
 * This marks the draft as published and sets the publishedAt timestamp
 */
export async function publishDraft(
  context: VideoContext,
  originalVideoId: string,
  draftId: string
): Promise<void> {
  const now = new Date().toISOString();
  await updateDraftStatus(context, originalVideoId, draftId, 'published', {
    publishedAt: now,
  });
}

/**
 * Delete a draft and its associated storage files
 */
export async function deleteDraft(
  context: VideoContext,
  originalVideoId: string,
  draftId: string
): Promise<void> {
  const collectionPath = getVideosCollectionPath(context);
  const draftRef = doc(db, collectionPath, originalVideoId, 'drafts', draftId);
  const snapshot = await getDoc(draftRef);

  if (snapshot.exists()) {
    // Read raw data to collect all storage paths before normalization wipes legacy fields
    const raw = snapshot.data() as Record<string, unknown>;

    const storagePaths: string[] = [];

    for (const segment of (raw['recordingSegments'] as Array<{ audioStoragePath?: string }> ?? [])) {
      if (segment.audioStoragePath) storagePaths.push(segment.audioStoragePath);
    }
    for (const snap of (raw['annotationSnapshots'] as Array<{ canvasStoragePath?: string }> ?? [])) {
      if (snap.canvasStoragePath) storagePaths.push(snap.canvasStoragePath);
    }
    for (const rec of (raw['audioRecordings'] as Array<{ audioStoragePath?: string }> ?? [])) {
      if (rec.audioStoragePath) storagePaths.push(rec.audioStoragePath);
    }
    for (const clip of (raw['voiceoverNotableSegmentClips'] as Array<{ videoStoragePath?: string }> ?? [])) {
      if (clip.videoStoragePath) storagePaths.push(clip.videoStoragePath);
    }

    await Promise.all(storagePaths.map((p) => deleteStorageFile(p)));
  }

  await deleteDoc(draftRef);
  lastKnownDrafts.delete(draftRef.path);
}

/**
 * Subscribe to real-time draft updates (for preview sharing)
 */
export function subscribeToDraft(
  context: VideoContext,
  originalVideoId: string,
  draftId: string,
  callback: (draft: VideoEditDraft | null) => void
): Unsubscribe {
  const collectionPath = getVideosCollectionPath(context);
  const draftRef = doc(
    db,
    collectionPath,
    originalVideoId,
    'drafts',
    draftId
  );

  return onSnapshot(draftRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(normalizeDraft(snapshot.data() as Record<string, unknown>));
    } else {
      callback(null);
    }
  });
}

/**
 * Fetch a blob from a URL
 */
async function fetchBlob(url: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch blob: ${response.status}`);
  }
  return response.blob();
}

/**
 * Load all assets for preview playback
 */
export async function loadDraftAssets(
  draft: VideoEditDraft
): Promise<{
  voiceoverUrls: Map<string, string>;
  snapshotUrls: Map<string, string>;
  audioRecordingUrls: Map<string, string>;
  voiceoverClipUrls: Map<string, string>;
}> {
  const voiceoverUrls = new Map<string, string>();
  const snapshotUrls = new Map<string, string>();
  const audioRecordingUrls = new Map<string, string>();
  const voiceoverClipUrls = new Map<string, string>();

  // Load voiceover URLs in parallel
  await Promise.all(
    draft.recordingSegments.map(async (segment) => {
      try {
        const url = await getStorageUrl(segment.audioStoragePath);
        voiceoverUrls.set(segment.id, url);
      } catch (error) {
        console.error('Failed to load voiceover:', segment.id, error);
      }
    })
  );

  // Load snapshot URLs in parallel
  await Promise.all(
    draft.annotationSnapshots.map(async (snapshot) => {
      try {
        const url = await getStorageUrl(snapshot.canvasStoragePath);
        snapshotUrls.set(snapshot.id, url);
      } catch (error) {
        console.error('Failed to load snapshot:', snapshot.id, error);
      }
    })
  );

  // Load audio recording URLs in parallel
  await Promise.all(
    draft.audioRecordings.map(async (recording) => {
      try {
        const url = await getStorageUrl(recording.audioStoragePath);
        audioRecordingUrls.set(recording.id, url);
      } catch (error) {
        console.error('Failed to load audio recording:', recording.id, error);
      }
    })
  );

  // Load voiceover clip video URLs in parallel
  await Promise.all(
    draft.voiceoverNotableSegmentClips.map(async (clip) => {
      if (!clip.videoStoragePath) return;
      try {
        const url = await getStorageUrl(clip.videoStoragePath);
        voiceoverClipUrls.set(clip.id, url);
      } catch (error) {
        console.error('Failed to load voiceover clip video:', clip.id, error);
      }
    })
  );

  return { voiceoverUrls, snapshotUrls, audioRecordingUrls, voiceoverClipUrls };
}

/**
 * Load voiceover clip blobs for editor playback
 */
export async function loadDraftAssetsAsBlobs(
  draft: VideoEditDraft
): Promise<{
  voiceoverClipBlobs: Map<string, Blob>;
}> {
  const voiceoverClipBlobs = new Map<string, Blob>();

  await Promise.all(
    draft.voiceoverNotableSegmentClips.map(async (clip) => {
      if (!clip.videoStoragePath) return;
      try {
        const url = await getStorageUrl(clip.videoStoragePath);
        const blob = await fetchBlob(url);
        voiceoverClipBlobs.set(clip.id, blob);
      } catch (error) {
        console.error('Failed to load voiceover clip blob:', clip.id, error);
      }
    })
  );

  return { voiceoverClipBlobs };
}
